import styled from '@emotion/styled/macro';
import { FormattedMessage, useIntl } from 'react-intl';
import { Heading, Flex, Box, Button, Spacer, Divider } from '@chakra-ui/react';

import InputFormikField from '../forms/InputFormikField';
import CircularIconButton from '../utils/CircularIconButton';
import ShowHideSidebarButton from '../layout/ShowHideSidebarButton';

import Tooltip from '../utils/Tooltip';

import { ReactComponent as SaveIcon } from '../../icons/save.svg';
import { ReactComponent as EditIcon } from '../../icons/edit-circled.svg';
import { ReactComponent as DeleteLoadIcon } from '../../icons/delete.svg';
import { ReactComponent as FileIcon } from '../../icons/file.svg';

import SecondaryIconButton from '../utils/SecondaryIconButton';
import AutoformIconNode from './utils/AutoformIconNode';

const ItemTopHeader = ({
    inputName,
    onItemSave,
    onItemDelete,
    isSubmitting,
    isEditVisible,
    isSaveVisible,
    isSaveDisabled,
    isDeleteVisible,
    itemDescription,
    itemTypeId,
    isMaster,
    isEditMode,
    onEditClick,
    onPropertiesClick,
    isPropertiesVisible,
}) => {
    const intl = useIntl();

    return (
        <>
            <Flex pt={3} pl={6} mt={!isEditVisible && 3} align="center">
                <ItemIconWrapper isMaster={isMaster}>
                    <AutoformIconNode itemTypeId={itemTypeId} />
                </ItemIconWrapper>
                {isEditMode ? (
                    <InputFormikField px={3} flex="1" maxW="500px" name={inputName} type="text" />
                ) : (
                    <Heading px={2} as="h2" variant="h2">
                        {itemDescription}
                    </Heading>
                )}

                {isEditVisible && (
                    <Box display="inline-block" mr={4}>
                        <Tooltip
                            label={intl.formatMessage({
                                id: 'common_edit_btn_tooltip_and_label',
                            })}
                        >
                            <CircularIconButton
                                aria-label={intl.formatMessage({
                                    id: 'common_edit_btn_tooltip_and_label',
                                })}
                                leftIcon={<EditIcon />}
                                onClick={() => onEditClick(!isEditMode)}
                            />
                        </Tooltip>
                    </Box>
                )}
            </Flex>

            <ActionBar>
                <ShowHideSidebarButton />
                {isPropertiesVisible && (
                    <SecondaryIconButton
                        leftIcon={<FileIcon />}
                        size="sm"
                        variant="solid"
                        onClick={onPropertiesClick}
                        flexShrink={0}
                    >
                        <FormattedMessage id="portfolio_item_properties" />
                    </SecondaryIconButton>
                )}

                <Spacer display={{ base: 'none', xl: 'block' }} />

                {isDeleteVisible && (
                    <Button
                        leftIcon={<DeleteLoadIcon />}
                        type="button"
                        variant="special"
                        onClick={onItemDelete}
                        size="sm"
                    >
                        <FormattedMessage id="common_delete_item_btn" />
                    </Button>
                )}

                {isSaveVisible && (
                    <Button
                        leftIcon={<SaveIcon />}
                        isLoading={isSubmitting}
                        onClick={onItemSave}
                        disabled={isSaveDisabled}
                        type="submit"
                        size="sm"
                    >
                        <FormattedMessage id="common_save_changes_button" />
                    </Button>
                )}
            </ActionBar>

            <Divider />
        </>
    );
};

const ItemIconWrapper = styled.div`
    svg {
        font-size: 24px;
        width: 1em;
        height: 1em;
        line-height: 1em;
        flex-shrink: 0;
        color: ${(props) => (props.isMaster ? 'var(--chakra-colors-green-500)' : 'var(--chakra-colors-gray-600)')};
    }
`;

const ActionBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    width: 100%;
    padding: 12px 24px;
    gap: 16px;

    button {
        text-transform: capitalize;
        width: 100%;
    }

    button:first-of-type {
        margin-top: 4px;
    }

    @media (min-width: 769px) {
        flex-direction: row;

        button:first-of-type {
            margin-top: 0px;
        }
    }

    @media (min-width: 1280px) {
        button {
            width: auto;
        }
    }
`;

export default ItemTopHeader;
